import { useState } from 'react'
import { useFormContext } from 'react-hook-form'

import { CasePriority } from '@avantstay/arriere-clients/dist/arriereBackoffice'

import {
  TAXONOMY_FIELD_NAMES,
  TaxonomyCategory,
  TaxonomyNatureOption,
  TaxonomySelection,
  TaxonomyServiceType,
  TaxonomyStatus,
  TaxonomySubcategory,
  TaxonomyTask,
} from './CaseTaxonomy.types'
import {
  defaultStatusForTask,
  naturesForSelection,
  priorityFromLevel,
  resolveSingleOptions,
} from './taxonomyTree'

interface TaxonomyCascadeOptions {
  /**
   * How a nature's default priority reaches the host form. The default writes
   * the plain CasePriority value the edit forms use; the creation form passes
   * a setter matching its own option shape.
   */
  applyDefaultPriority?: (priority: CasePriority) => void
  /**
   * Called whenever the selected task changes (with that task's "New" status,
   * or undefined when there is none). Creation passes a setter so the
   * taxonomy status defaults on classify/reclassify; edit omits it to keep
   * the saved status untouched.
   */
  applyDefaultStatus?: (status: TaxonomyStatus | undefined) => void
}

/**
 * The cascade mechanics of the taxonomy fields: committing a selection
 * (Record type -> Service type -> Category -> Subcategory -> Nature),
 * auto-selecting single-option levels, and updating the case priority to the
 * nature's default only on user-driven changes or AI suggestions (hydration
 * keeps the saved priority untouched).
 */
export const useTaxonomyCascade = (options?: TaxonomyCascadeOptions) => {
  const { watch, getValues, setValue, clearErrors } = useFormContext()

  const [isUserOverridden, setIsUserOverridden] = useState(false)

  const selectedTask = watch('taxonomyTask') as TaxonomyTask | null | undefined
  const selectedServiceType = watch('taxonomyServiceType') as TaxonomyServiceType | null | undefined
  const selectedCategory = watch('taxonomyCategory') as TaxonomyCategory | null | undefined
  const selectedSubcategory = watch('taxonomySubcategory') as TaxonomySubcategory | null | undefined

  const applyDefaultPriority = (nature: TaxonomyNatureOption | null | undefined) => {
    const defaultPriority = priorityFromLevel(nature?.defaultPriority)
    if (!defaultPriority) {
      return
    }
    if (options?.applyDefaultPriority) {
      options.applyDefaultPriority(defaultPriority)
    } else {
      setValue('priority', defaultPriority)
    }
  }

  const commitSelection = (selection: TaxonomySelection) => {
    const natures = naturesForSelection(selection)
    const nature = selection.nature ?? (natures.length === 1 ? natures[0] : null)
    const currentTask = getValues('taxonomyTask') as TaxonomyTask | null | undefined
    const hasTaskChanged = (selection.task?.label ?? null) !== (currentTask?.label ?? null)

    setValue('taxonomyTask', selection.task ?? null)
    setValue('taxonomyServiceType', selection.serviceType ?? null)
    setValue('taxonomyCategory', selection.category ?? null)
    setValue('taxonomySubcategory', selection.subcategory ?? null)
    setValue('taxonomyNature', nature)
    clearErrors(TAXONOMY_FIELD_NAMES)

    if (hasTaskChanged) {
      options?.applyDefaultStatus?.(defaultStatusForTask(selection.task))
    }

    return nature
  }

  /** Applies the saved case selection on hydration — not a user override. */
  const applySelection = (selection: TaxonomySelection) => {
    setIsUserOverridden(false)
    commitSelection(selection)
  }

  /** Applies the AI-suggested selection: fills the cascade plus the defaults. */
  const applySuggestion = (selection: TaxonomySelection) => {
    setIsUserOverridden(false)
    const nature = commitSelection(resolveSingleOptions(selection))
    applyDefaultPriority(nature)
  }

  const clearSelection = () => {
    commitSelection({})
  }

  const resetUserOverride = () => {
    setIsUserOverridden(false)
  }

  const applyCascade = (selection: TaxonomySelection) => {
    setIsUserOverridden(true)
    const nature = commitSelection(resolveSingleOptions(selection))
    applyDefaultPriority(nature)
  }

  const handleTaskChange = (task?: TaxonomyTask) => {
    applyCascade(task ? { task } : {})
  }

  const handleServiceTypeChange = (serviceType?: TaxonomyServiceType) => {
    applyCascade({ task: selectedTask ?? undefined, serviceType })
  }

  const handleCategoryChange = (category?: TaxonomyCategory) => {
    applyCascade({
      task: selectedTask ?? undefined,
      serviceType: selectedServiceType ?? undefined,
      category,
    })
  }

  const handleSubcategoryChange = (subcategory?: TaxonomySubcategory) => {
    applyCascade({
      task: selectedTask ?? undefined,
      serviceType: selectedServiceType ?? undefined,
      category: selectedCategory ?? undefined,
      subcategory,
    })
  }

  const handleNatureChange = (nature?: TaxonomyNatureOption) => {
    setIsUserOverridden(true)
    setValue('taxonomyNature', nature ?? null)
    clearErrors(['taxonomyNature'])
    applyDefaultPriority(nature)
  }

  return {
    isUserOverridden,
    applySelection,
    applySuggestion,
    clearSelection,
    resetUserOverride,
    selections: {
      task: selectedTask,
      serviceType: selectedServiceType,
      category: selectedCategory,
      subcategory: selectedSubcategory,
    },
    handlers: {
      handleTaskChange,
      handleServiceTypeChange,
      handleCategoryChange,
      handleSubcategoryChange,
      handleNatureChange,
    },
  }
}
